import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import {
  GATEWAY_TYPE_CYBERSOURCE,
  GATEWAY_TYPE_SQUARE,
  GATEWAY_TYPE_STRIPE,
  GATEWAY_TYPE_ADYEN,
  GATEWAY_TYPE_WORLDPAY,
  GATEWAY_TYPE_PAYPAL,
} from './constants/gatewayTypes';
import ShowclixCreditCard from './components/ShowclixCreditCard';
import SquareCreditCard from './components/SquareCreditCard';
import StripeCreditCard from './components/StripeCreditCard';
import WorldpayCreditCard from './components/WorldpayCreditCard';
import PaypalCreditCard from './components/PayPalCreditCard';
import CreditCards, { checkoutImagesUrl } from './components/CreditCards';
import AdyenCreditCard from './components/AdyenCreditCard';
import CardinalCommerceCreditCard from './components/CardinalCommerceCreditCard';
import Radio from '../Radio';
import Message from '../CheckoutProcess/components/Message';
import Translation from '../CheckoutProcess/components/Translation';
import { PaymentContext } from './contexts/PaymentContext';

class Payment extends Component {
  state = {
    selectedGatewayType: null,
  };

  constructor(props) {
    super(props);
    this.setPaymentHandlesSubmission();
  }

  setPaymentHandlesSubmission = () => {
    const gatewayType = +this.getSelectedGatewayType();
    const isStripe = gatewayType === GATEWAY_TYPE_STRIPE && !!this.props.gatewayKey;
    const isPayPal = gatewayType === GATEWAY_TYPE_PAYPAL && !!this.getAltGatewayField(GATEWAY_TYPE_PAYPAL, 'key');
    this.props.setPaymentHandlesSubmission(isStripe || isPayPal);
  };

  componentDidMount() {
    window.scrollTo(0, 0);
    const validTypes = this.validGatewayTypes();
    if (validTypes.length === 1) {
      // auto select
      this.onGatewayTypeChange(validTypes[0]);
    }
  }

  getSelectedGatewayType = () => {
    return this.state.selectedGatewayType ?? this.props.gatewayType;
  };

  getAltGatewayField = (type, field) => {
    return this.props.altGateways.find((g) => +g.type === +type)?.[field];
  };

  onGatewayTypeChange = (gatewayType) => {
    this.props.onGatewayTypeChange(gatewayType);
    this.setState({ selectedGatewayType: gatewayType }, () => {
      this.setPaymentHandlesSubmission();
    });
  };

  render() {
    return (
      <Fragment>
        {this.renderPaymentOptions()}
        {this.renderSelectedGateway()}
      </Fragment>
    );
  }

  validGatewayTypes = () => {
    const supportedAltGatewayTypes = [GATEWAY_TYPE_PAYPAL];

    const filteredAltGatewayTypes = this.props.altGateways
      .filter((altGateway) => {
        return supportedAltGatewayTypes.includes(+altGateway.type);
      })
      .map((altGateway) => +altGateway.type);

    let validTypes = [];

    // if we have alt gateways but no default gateways, just use the alt gateways
    if (!this.props.gatewayType && filteredAltGatewayTypes.length) {
      validTypes = filteredAltGatewayTypes;
    } else {
      // default behavior, always show default gateway
      validTypes = [+this.props.gatewayType].concat(filteredAltGatewayTypes);
    }
    return [...new Set(validTypes)];
  };

  renderPaymentOptions = () => {
    const gatewayTypes = this.validGatewayTypes();

    // no need to show options if there aren't multiple gateways
    if (gatewayTypes.length < 2) {
      return null;
    }
    return (
      <section className="checkout-form__section">
        <h4 className="checkout-form__sub-heading">
          <Translation>{'checkout__payment_method'}</Translation>
        </h4>
        <div className="payment-options__radio">
          {gatewayTypes.map((gatewayType, i) => {
            return (
              <div className="payment-options__radio-input" key={i}>
                <Radio
                  id={`gateway_${gatewayType}`}
                  name="payment-option"
                  value={gatewayType || ''}
                  onChange={() => {
                    this.onGatewayTypeChange(gatewayType ? gatewayType : null);
                  }}
                  checked={+this.getSelectedGatewayType() === +gatewayType}
                  selected={+this.getSelectedGatewayType() === +gatewayType}
                  label={this.getPaymentOptionLabel(gatewayType)}
                />
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  getPaymentOptionLabel = (gatewayType) => {
    if (+gatewayType === GATEWAY_TYPE_PAYPAL) {
      return (
        <div className="cc-payment--paypal">
          <span className="icon--paypal">
            <img src={`${checkoutImagesUrl}cc-logo--paypal-horizontal@2x.png`} />
          </span>{' '}
          <span className="sr-only">PayPal</span>
        </div>
      );
    }
    if (+gatewayType === GATEWAY_TYPE_STRIPE) {
      /**
       * Stripe payment element displays accepted card icons by default
       * so for stripe, we will just show a generic credit card icon for the
       * radio button label
       */
      return (
        <div className="cc-payment--card">
          <span className="icon--cc">
            <img src={`${checkoutImagesUrl}cc-payment--card@2x.png`} />
          </span>{' '}
          <Translation>{'checkout__pay_by_card'}</Translation>
        </div>
      );
    }
    return <CreditCards acceptedCards={this.getAcceptedCards()} />;
  };

  getAcceptedCards = () => {
    let acceptedCards = { ...this.props.acceptedCards };

    if (acceptedCards.apple_pay) {
      delete acceptedCards.apple_pay;
    }
    if (acceptedCards.google_pay) {
      delete acceptedCards.google_pay;
    }

    return acceptedCards;
  };

  validateAndSaveDeliveryAddress = (t, validate) => {
    return this.props.validateAndSaveDeliveryAddress(t, validate);
  };

  renderSelectedGateway() {
    const selectedGatewayType = this.getSelectedGatewayType();

    const paymentProps = {
      onPaymentInitiated: this.props.onPaymentInitiated,
      onPaymentSuccess: this.props.onPaymentSuccess,
      onPaymentFail: this.props.onPaymentFail,
      onPaymentSubmit: this.props.onPaymentSubmit,
      email: this.props.email,
      name: this.props.name,
      phone: this.props.phone,
      token: this.props.token,
      secret: this.props.secret,
      amount: this.props.amount,
      tosAccepted: this.props.tosAccepted,
      leapOptIn: this.props.leapOptIn,
      apiUrl: this.props.apiUrl,
      acceptedCards: this.props.acceptedCards,
      captureMethod: this.props.captureMethod,
      isFullyPaid: this.props.isReservationFullyPaid,
      createdNewAccount: this.props.createdNewAccount,
      emailOptOut: this.props.emailOptOut,
      currency: this.props.currency,
      defaultAddress: this.props.defaultAddress,
      isPhoneRequired: this.props.isPhoneRequired,
    };

    if (+selectedGatewayType === GATEWAY_TYPE_STRIPE && this.props.gatewayKey) {
      /**
       * If reservation isn't loaded, don't render StripeCreditCard
       */
      if (!this.props.token || this.props.reservationIsCompleted) {
        return null;
      }

      /**
       * If pending payment, don't render StripeCreditCard because we don't need it
       * creating or canceling any payment intents.
       */
      if (this.props.reservationPendingPaymentCompletion) {
        return <Message inlineText info title="Payment pending" />;
      }

      return (
        <StripeCreditCard
          {...paymentProps}
          country={this.props.country}
          publicKey={this.props.gatewayKey}
          paymentIntentSecret={this.props.paymentIntentSecret}
          paymentIntentId={this.props.paymentIntentId}
          createStripePaymentIntent={this.props.createStripePaymentIntent}
          cancelStripePaymentIntent={this.props.cancelStripePaymentIntent}
          finalizeReservationFromPaymentIntent={this.props.finalizeReservationFromPaymentIntent}
          setStripePaymentIntentSecret={this.props.setStripePaymentIntentSecret}
          setStripePaymentIntentId={this.props.setStripePaymentIntentId}
          loading={this.props.loading}
          stripeSubmittedPayment={this.props.stripeSubmittedPayment}
          setStripeSubmittedPayment={this.props.setStripeSubmittedPayment}
          locale={this.props.locale}
          validateAndSaveDeliveryAddress={this.validateAndSaveDeliveryAddress}
          setDeliveryAddress={this.props.setDeliveryAddress}
          deliveryAddress={this.props.deliveryAddress}
          deliveryOption={this.props.deliveryOption}
          hasDeliverableProducts={this.props.hasDeliverableProducts}
          onReservationWait={this.props.onReservationWait}
          useElementsLoading={this.props.useElementsLoading}
        />
      );
    }

    if (+selectedGatewayType === GATEWAY_TYPE_SQUARE && this.props.gatewayKey && this.props.gatewayLocation) {
      return (
        <PaymentContext.Consumer>
          {({ TranslationContext }) => (
            <TranslationContext.Consumer>
              {(t) => (
                <SquareCreditCard
                  {...paymentProps}
                  applicationId={this.props.gatewayKey}
                  locationId={this.props.gatewayLocation}
                  country={this.props.gatewayCountry}
                  locale={this.props.gatewayLocale}
                  merchantDescriptor={this.props.merchantDescriptor}
                  jsUrl={this.props.jsUrl}
                  wallets={this.props.wallets}
                  validateAndSaveDeliveryAddress={this.validateAndSaveDeliveryAddress}
                  setDeliveryAddress={this.props.setDeliveryAddress}
                  deliveryAddress={this.props.deliveryAddress}
                  deliveryOption={this.props.deliveryOption}
                  hasDeliverableProducts={this.props.hasDeliverableProducts}
                  t={t}
                />
              )}
            </TranslationContext.Consumer>
          )}
        </PaymentContext.Consumer>
      );
    }

    if (+selectedGatewayType === GATEWAY_TYPE_CYBERSOURCE) {
      return (
        <CardinalCommerceCreditCard
          {...paymentProps}
          jsUrl={this.props.jsUrl}
          onGetCardinalData={this.props.onGetCardinalData}
        />
      );
    }

    if (+selectedGatewayType === GATEWAY_TYPE_ADYEN) {
      return (
        <AdyenCreditCard
          {...paymentProps}
          clientKey={this.props.gatewayKey}
          environment={this.props.gatewayEnvironment}
          originUrl={this.props.originUrl}
          onThreeDSRequest={this.props.onThreeDSRequest}
        />
      );
    }

    if (+selectedGatewayType === GATEWAY_TYPE_WORLDPAY && this.props.gatewayKey) {
      return (
        <WorldpayCreditCard
          {...paymentProps}
          jsUrl={this.props.jsUrl}
          gatewayKey={this.props.gatewayKey}
          validateAndSaveDeliveryAddress={this.validateAndSaveDeliveryAddress}
          setDeliveryAddress={this.props.setDeliveryAddress}
          deliveryAddress={this.props.deliveryAddress}
          deliveryOption={this.props.deliveryOption}
          hasDeliverableProducts={this.props.hasDeliverableProducts}
        />
      );
    }

    if (
      +selectedGatewayType === GATEWAY_TYPE_PAYPAL &&
      !!this.getAltGatewayField(GATEWAY_TYPE_PAYPAL, 'key') &&
      !!this.getAltGatewayField(GATEWAY_TYPE_PAYPAL, 'wallets')
    ) {
      return (
        <PaymentContext.Consumer>
          {({ TranslationContext }) => (
            <TranslationContext.Consumer>
              {(t) => (
                <PaypalCreditCard
                  {...paymentProps}
                  loading={this.props.loading}
                  gatewayKey={this.getAltGatewayField(GATEWAY_TYPE_PAYPAL, 'key')}
                  validateAndSaveDeliveryAddress={this.validateAndSaveDeliveryAddress}
                  setDeliveryAddress={this.props.setDeliveryAddress}
                  deliveryAddress={this.props.deliveryAddress}
                  fundingSources={this.getAltGatewayField(GATEWAY_TYPE_PAYPAL, 'wallets')}
                  t={t}
                />
              )}
            </TranslationContext.Consumer>
          )}
        </PaymentContext.Consumer>
      );
    }

    return (
      <PaymentContext.Consumer>
        {({ TranslationContext }) => (
          <TranslationContext.Consumer>
            {(t) => (
              <ShowclixCreditCard
                {...paymentProps}
                validateAndSaveDeliveryAddress={this.validateAndSaveDeliveryAddress}
                setDeliveryAddress={this.props.setDeliveryAddress}
                deliveryAddress={this.props.deliveryAddress}
                deliveryOption={this.props.deliveryOption}
                hasDeliverableProducts={this.props.hasDeliverableProducts}
                t={t}
              />
            )}
          </TranslationContext.Consumer>
        )}
      </PaymentContext.Consumer>
    );
  }
}

Payment.propTypes = {
  amount: PropTypes.number.isRequired,
  email: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  secret: PropTypes.string.isRequired,
  tosAccepted: PropTypes.bool.isRequired,
  useElementsLoading: PropTypes.bool
};

export default Payment;
